/**
 * PDP v2 set row and the Add to bag buttons (plan 001 A7). Mat, 2026-10-23:
 * a shopper who ticks colours and then chooses a size presses the Add to bag
 * under the sizes, so that button has to add the set. Loaded with the
 * multi-add only, after pdp-v2-set-api.js and before pdp-v2-set.js, which
 * calls in here (theme.pdpV2.setButtons).
 *
 * - While the multi-add holds ticked colours it can add, the main and sticky
 *   Add to bag carry the row's own button label ("Add 3 to bag — $378.00")
 *   and their submit becomes the row's one set POST (A3, E4); pdp_add names
 *   the button pressed. Nothing ticked, or the main product sold out in the
 *   chosen size: both buttons are exactly what main.js and sticky-atc.js make
 *   them. This reverses 019 S-3 for the multi-add only.
 * - No size chosen: the button's own check asks for one (J1/J2). The panel's
 *   "Choose your size" opens the sticky bar's sizes while that bar is up,
 *   else the main picker's check (focus on the first size in stock).
 * - Shopify's dynamic checkout ("Buy with Shop", Apple Pay, PayPal) buys the
 *   main product only, so while the buttons carry the set it is hidden in
 *   place (Mat, 2026-10-25): its box stays, so nothing below moves, and the
 *   block's note sits in it. The state is an attribute on <buy-buttons>, so a
 *   button main.js inserts later (on the first size) arrives hidden; the note
 *   and inert follow it in.
 */
(function () {
  'use strict';

  var theme = window.theme = window.theme || {};
  var api = theme.pdpV2;
  if (!api || api.setButtons) return;

  // The owner's label, saved on the button the first time it carries the set
  // (an attribute, so a restored snapshot restores it too).
  var SAVED = 'data-set-label';
  var EXPRESS = 'data-set-express';
  var NOTE = 'pdp-v2-express-note';
  var rows = [];

  function stickyOf(row) {
    var section = row.closest('.section-main-product');
    return section ? section.querySelector('sticky-atc') : null;
  }

  function pickerOf(row) {
    return row.form ? row.form.querySelector('variant-picker') : null;
  }

  /**
   * The row's own button label while its multi-add holds ticked colours it
   * can add, else null: nothing ticked, another tab, an ended offer, or the
   * main product sold out in the size chosen above (no set can be added).
   */
  function carried(row) {
    if (!row._connected || row.expired || typeof row.multi !== 'function' || !row.multi() || !row.picks().length) return null;
    var pant = row.pantUnit();
    if (pant && pant.chosen && !pant.available) return null;
    var cta = row.querySelector('[data-cta]');
    return cta ? cta.textContent : null;
  }

  // main.js keeps the main button's text in .btn__main-text when present.
  function labelEl(btn) {
    return btn.querySelector('.btn__main-text') || btn;
  }

  function carry(btn, text) {
    if (!btn) return;
    var el = labelEl(btn);
    if (!btn.hasAttribute(SAVED)) btn.setAttribute(SAVED, el.textContent);
    if (el.textContent !== text) el.textContent = text;
  }

  // The sticky bar shows its own price at 768px and up, so there the total
  // (" — $378.00") is a span the CSS hides, keeping the bar its usual height.
  function carrySticky(btn, text) {
    if (!btn || btn.textContent === text) return;
    if (!btn.hasAttribute(SAVED)) btn.setAttribute(SAVED, btn.textContent);
    var at = text.lastIndexOf(' — ');
    btn.textContent = at > 0 ? text.slice(0, at) : text;
    if (at <= 0) return;
    var total = document.createElement('span');
    total.className = 'pdp-v2-sticky-total';
    total.textContent = text.slice(at);
    btn.appendChild(total);
  }

  // Back to the owner's label: its own writer when it has one that applies, else the saved text.
  function restore(btn, writer) {
    if (!btn || !btn.hasAttribute(SAVED)) return;
    var saved = btn.getAttribute(SAVED);
    btn.removeAttribute(SAVED);
    if (!writer()) labelEl(btn).textContent = saved;
  }

  function buyButtonsOf(row) {
    return row.form ? row.form.querySelector('buy-buttons') : null;
  }

  /**
   * Shopify's dynamic checkout, hidden in place while the set is carried: the
   * CSS hides every child of its wrapper but the note, and inert keeps them
   * out of reach. No wrapper yet (no size chosen): the attribute alone.
   */
  function express(row, on) {
    var host = buyButtonsOf(row);
    if (!host) return;
    if (on) host.setAttribute(EXPRESS, ''); else host.removeAttribute(EXPRESS);
    var wrap = host.querySelector('.shopify-payment-button');
    if (!wrap) return;
    var note = wrap.querySelector('.' + NOTE);
    var copy = (row.config || {}).copy || {};
    var text = copy.expressNote;
    if (on && !note && text) {
      note = document.createElement('p');
      note.className = NOTE;
      [[copy.expressTitle, '__title'], [text, '__text']].forEach(function (part) {
        if (!part[0]) return;
        var span = document.createElement('span');
        span.className = NOTE + part[1];
        span.textContent = part[0];
        note.appendChild(span);
      });
      wrap.appendChild(note);
    }
    [].forEach.call(wrap.children, function (el) {
      if (el === note) return;
      if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
    });
  }

  /** Both buttons follow the row: its label while it carries ticked colours, else their own. */
  function sync(row) {
    var bar = stickyOf(row);
    var main = row.form ? row.form.querySelector('buy-buttons [name="add"]') : null;
    var stuck = bar ? bar.querySelector('[name="add"]') : null;
    var text = carried(row);
    express(row, !!text);
    if (text) {
      carry(main, text);
      carrySticky(stuck, text);
      return;
    }
    var picker = pickerOf(row);
    var variant = picker ? picker.variant : null;
    // VariantPicker writes nothing until a size is chosen, so the saved text stands in.
    restore(main, function () {
      if (!variant || typeof picker.updateAddToCartButton !== 'function') return false;
      picker.updateAddToCartButton();
      return true;
    });
    restore(stuck, function () {
      if (typeof bar.updateAddToCartButton !== 'function') return false;
      bar.updateAddToCartButton(variant || null);
      return true;
    });
  }

  /**
   * Capture phase on the main <product-form> and on <sticky-atc>, so it runs
   * before main.js's own submit handler on the inner <form>. Takes only the
   * Add to bag form's own submit (the product form also holds the instalments
   * banner's forms), and only when the row carries a set and a size is chosen.
   */
  function onSubmit(row, source) {
    return function (evt) {
      var form = evt.target;
      if (!form || !form.classList || !form.classList.contains('js-product-form')) return;
      var btn = evt.submitter || form.querySelector('[name="add"]');
      if (!btn || btn.getAttribute('name') !== 'add' || !carried(row)) return;
      var pant = row.pantUnit();
      if (!pant || !pant.chosen) return;
      evt.preventDefault();
      evt.stopPropagation();
      if (btn.getAttribute('aria-disabled') === 'true') return;
      var done = row.submit(btn, source);
      if (!done) return;
      // The main.js button states: loading, then a tick on success.
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');
      done.then(function (outcome) {
        btn.removeAttribute('aria-disabled');
        if (outcome && outcome.kind === 'confirmed') {
          btn.classList.add('is-success');
          setTimeout(function () { btn.classList.remove('is-loading'); btn.classList.remove('is-success'); }, 2000);
        } else {
          btn.classList.remove('is-loading');
        }
      });
    };
  }

  /** "Choose your size": the sticky bar's sizes while the bar is up (no scrolling), else the main picker's check. */
  function toSize(row) {
    var bar = stickyOf(row);
    var open = bar ? bar.querySelector('.pdp-v2-sticky-size') : null;
    var root = document.documentElement;
    if (open && open.getClientRects().length && root && root.classList.contains('pdp-v2-sticky-in')) {
      if (open.getAttribute('aria-expanded') !== 'true') open.click();
      return;
    }
    if (row.form && typeof row.form.validate === 'function') row.form.validate();
  }

  api.setButtons = {
    sync: sync,
    toSize: toSize,

    attach: function (row) {
      if (row._setButtons) return;
      var bar = stickyOf(row);
      var h = row._setButtons = {
        bar: bar,
        main: onSubmit(row, 'pdp_main'),
        sticky: onSubmit(row, 'sticky'),
        click: function (evt) {
          var t = evt.target;
          if (t && t.closest && t.closest('[data-to-size]')) toSize(row);
        }
      };
      if (row.form) row.form.addEventListener('submit', h.main, true);
      if (bar) bar.addEventListener('submit', h.sticky, true);
      row.addEventListener('click', h.click);
      // main.js inserts the dynamic checkout on the first size (or later, if
      // its lazy init runs after): the note and inert follow it in.
      var host = buyButtonsOf(row);
      if (host && window.MutationObserver) {
        h.observer = new window.MutationObserver(function () {
          if (host.hasAttribute(EXPRESS)) express(row, true);
        });
        h.observer.observe(host, { childList: true, subtree: true });
      }
      rows.push(row);
    },

    /** On disconnect: the listeners go and both buttons get their own labels back. */
    release: function (row) {
      var h = row._setButtons;
      if (!h) return;
      row._setButtons = null;
      if (row.form) row.form.removeEventListener('submit', h.main, true);
      if (h.bar) h.bar.removeEventListener('submit', h.sticky, true);
      row.removeEventListener('click', h.click);
      if (h.observer) h.observer.disconnect();
      var at = rows.indexOf(row);
      if (at >= 0) rows.splice(at, 1);
      sync(row);
    }
  };

  // A size change rewrites both labels: VariantPicker before its event, and
  // sticky-atc.js on the section while the event bubbles past the row's
  // form. The document hears it last, so the carried label goes back on.
  document.addEventListener('on:variant:change', function () {
    rows.slice().forEach(sync);
  });
})();
